import React from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { API } from "@/lib/api";
import { Star } from "lucide-react";

/**
 * Public alumni testimonial submission page.
 * URL: /testimoni/:bookingId
 * Token = booking.id (UUID). Server validates booking is confirmed + departure in past.
 */
export default function AlumniTestimoni() {
  const { bookingId } = useParams();
  const [prefill, setPrefill] = React.useState(null);
  const [err, setErr] = React.useState("");
  const [rating, setRating] = React.useState(5);
  const [text, setText] = React.useState("");
  const [photoUrl, setPhotoUrl] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(false);

  React.useEffect(() => {
    fetch(`${API}/alumni/testimoni/${bookingId}`)
      .then(async (r) => {
        if (!r.ok) {
          const data = await r.json().catch(() => ({}));
          setErr(data.detail || `Error ${r.status}`);
          return null;
        }
        return r.json();
      })
      .then((data) => {
        if (data) {
          setPrefill(data);
          if (data.already_submitted) setDone(true);
        }
      })      .catch(() => setErr("Gagal menghubungi server."));
  }, [bookingId]);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await fetch(`${API}/alumni/testimoni/${bookingId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rating, text_id: text, photo_url: photoUrl || null }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        setErr(data.detail || `Error ${r.status}`);
        return;
      }
      setDone(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-muted/20 py-16 px-4">
      <Helmet>
        <title>Tulis Testimoni Umroh — BATIK Tour & Travel</title>
      </Helmet>
      <div className="max-w-xl mx-auto bg-card border border-border rounded-xl shadow-xl p-8">
        <div className="mb-6">
          <p className="overline text-accent">Alumni BATIK</p>
          <h1 className="font-serif text-3xl text-primary mt-1">Bagikan Cerita Umroh Anda</h1>
        </div>

        {err && (
          <div
            className="bg-red-50 border border-red-200 text-red-700 rounded-md p-4 text-sm"
            data-testid="alumni-error"
          >
            {err}
          </div>
        )}

        {!err && !prefill && (
          <p className="text-sm text-muted-foreground">Memuat…</p>
        )}

        {!err && prefill && done && (
          <div
            className={`rounded-lg p-6 text-center border ${
              prefill.status === "rejected"
                ? "bg-amber-50 border-amber-200"
                : prefill.status === "approved"
                ? "bg-emerald-50 border-emerald-200"
                : "bg-sky-50 border-sky-200"
            }`}
            data-testid="alumni-success"
          >
            <h2
              className={`font-serif text-xl mb-2 ${
                prefill.status === "rejected"
                  ? "text-amber-800"
                  : prefill.status === "approved"
                  ? "text-emerald-800"
                  : "text-sky-800"
              }`}
            >
              {prefill.status === "rejected"
                ? `Mohon maaf, ${prefill.name}`
                : `Jazakumullahu khairan, ${prefill.name}!`}
            </h2>
            <p
              className={`text-sm ${
                prefill.status === "rejected"
                  ? "text-amber-900/80"
                  : prefill.status === "approved"
                  ? "text-emerald-900/80"
                  : "text-sky-900/80"
              }`}
            >
              {prefill.status === "rejected"
                ? "Testimoni Anda belum sesuai untuk kami publikasikan. Silakan hubungi admin kami bila ingin merevisi."
                : prefill.status === "approved"
                ? "Testimoni Anda sudah tayang di website & blog BATIK Tour & Travel. Terima kasih telah berbagi cerita."
                : "Testimoni Anda sudah kami terima dan sedang kami review. Setelah disetujui, akan tampil di website & blog BATIK Tour & Travel."}
            </p>
          </div>
        )}

        {!err && prefill && !done && (
          <form onSubmit={submit} className="space-y-5" data-testid="alumni-form">
            <div className="text-sm bg-amber-50 border border-amber-200 rounded-md p-4">
              <p className="text-primary">
                Assalamu'alaikum <b>{prefill.name}</b>,
              </p>
              <p className="mt-1 text-muted-foreground">
                Booking: <b>{prefill.package_name}</b> &middot; Berangkat:{" "}
                <b>{prefill.departure_date}</b>
              </p>
            </div>

            <div>
              <label className="text-sm font-medium block mb-2">Rating Pelayanan</label>
              <div className="flex gap-1" data-testid="alumni-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setRating(n)}
                    className="p-1"
                    aria-label={`${n} bintang`}
                    data-testid={`alumni-star-${n}`}
                  >
                    <Star
                      className={`h-7 w-7 ${
                        n <= rating ? "fill-amber-400 text-amber-500" : "text-gray-300"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium block mb-1">Cerita &amp; Testimoni</label>
              <textarea
                required
                minLength={10}
                maxLength={2000}
                rows={6}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Ceritakan pengalaman Anda bersama BATIK Tour & Travel — pelayanan, pembimbing, hotel, momen favorit…"
                className="w-full border border-input rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-accent"
                data-testid="alumni-text"
              />
              <p className="text-xs text-muted-foreground mt-1">{text.length} / 2000 karakter</p>
            </div>

            <div>
              <label className="text-sm font-medium block mb-1">Link Foto (opsional)</label>
              <input
                type="url"
                value={photoUrl}
                onChange={(e) => setPhotoUrl(e.target.value)}
                placeholder="https://… (foto momen favorit Anda, bila ada)"
                className="w-full border border-input rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-accent"
                data-testid="alumni-photo"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Upload foto ke Google Drive / Google Photos lalu paste link di sini.
              </p>
            </div>

            <button
              type="submit"
              disabled={busy || text.length < 10}
              className="btn-primary w-full"
              data-testid="alumni-submit"
            >
              {busy ? "Mengirim…" : "Kirim Testimoni"}
            </button>

            <p className="text-xs text-muted-foreground text-center">
              Dengan mengirim, Anda setuju testimoni ini kami gunakan di website &amp; blog BATIK.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
